import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";

type TransitionInput = Parameters<
  ReturnType<typeof trpc.finding.transition.useMutation>["mutate"]
>[0];
type TransitionOutput = NonNullable<
  ReturnType<typeof trpc.finding.transition.useMutation>["data"]
>;

/** The last user batch of this tab, for `z` / ⌘Z undo in the queue. */
let lastBatch: { projectId: string; batchId: string } | null = null;

/**
 * `finding.transition` with list/queue/detail invalidation and an undo
 * toast. Undo reverses the whole batch (canonicals not moved since).
 */
export function useTransitionFindings(projectId: string) {
  const utils = trpc.useUtils();
  const refresh = () => {
    utils.finding.list.invalidate({ projectId });
    utils.finding.queueCounts.invalidate({ projectId });
    utils.finding.queue.invalidate({ projectId });
    utils.finding.getById.invalidate();
    utils.finding.explainPriority.invalidate();
    utils.guidance.list.invalidate({ projectId });
    utils.walkthrough.getById.invalidate();
    utils.pageFindings.routes.invalidate({ projectId });
    utils.pageFindings.forRoute.invalidate();
    utils.workflow.projectActivity.invalidate();
    utils.workflow.timeline.invalidate();
  };
  const undo = trpc.finding.undoTransition.useMutation({
    onSuccess: () => {
      refresh();
      toast.success("Undone");
    },
    onError: (err) => toastTrpcError(err, "Failed to undo transition"),
  });
  const mutation = trpc.finding.transition.useMutation({
    onSuccess: refresh,
    onError: (err) => toastTrpcError(err, "Failed to update findings"),
  });

  return {
    isPending: mutation.isPending,
    run(
      input: TransitionInput,
      message: string,
      onDone?: (result: TransitionOutput) => void,
    ) {
      mutation.mutate(input, {
        onSuccess: (res) => {
          lastBatch = { projectId, batchId: res.batchId };
          onDone?.(res);
          const skipped = res.skipped?.length ?? 0;
          toast.success(
            skipped > 0 ? `${message} (${skipped} skipped)` : message,
            {
              action: {
                label: "Undo",
                onClick: () => undo.mutate({ projectId, batchId: res.batchId }),
              },
            },
          );
        },
      });
    },
    /** Undo this tab's last batch in this project; false when none. */
    undoLast(): boolean {
      if (!lastBatch || lastBatch.projectId !== projectId) return false;
      undo.mutate(lastBatch);
      lastBatch = null;
      return true;
    },
  };
}
